import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  KeyRound,
  PlugZap,
  Trash2,
  ShieldCheck,
  AlertTriangle,
  RefreshCw,
  Search,
  Lock,
  Eye,
  EyeOff,
  Server,
} from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Loading,
  PageHeader,
  Panel,
  PanelBody,
  SectionLabel,
  StatusPill,
} from '../ui/index.js';
import { fetchJson, me } from '../api.js';

interface PortalCredentialRow {
  readonly key: string;
  readonly label: string;
  readonly configured: boolean;
  readonly username: string | null;
  readonly updatedAt: string | null;
  readonly updatedBy: string | null;
  readonly lastRun: {
    readonly status: string;
    readonly mode: string;
    readonly error: string | null;
    readonly startedAt: string;
    readonly finishedAt: string | null;
  } | null;
}

interface PortalCredentialList {
  readonly masterKeySet: boolean;
  readonly portals: readonly PortalCredentialRow[];
}

export function SettingsPortalsPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterState, setFilterState] = useState<'all' | 'configured' | 'pending' | 'failed'>('all');

  const meQuery = useQuery({ queryKey: ['me'], queryFn: me });
  const isAdmin = meQuery.data?.role === 'admin';

  const credentialsQuery = useQuery({
    queryKey: ['portal-credentials'],
    queryFn: () => fetchJson<PortalCredentialList>('/api/v1/portals/credentials'),
    refetchInterval: 8000,
  });

  const [editingPortal, setEditingPortal] = useState<PortalCredentialRow | null>(null);
  const [portalUsername, setPortalUsername] = useState('');
  const [portalPassword, setPortalPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const saveCredential = useMutation({
    mutationFn: (input: { key: string; username: string; password: string }) =>
      fetchJson(`/api/v1/portals/credentials/${input.key}`, {
        method: 'PUT',
        body: JSON.stringify({ username: input.username, password: input.password }),
      }),
    onSuccess: () => {
      setEditingPortal(null);
      setPortalUsername('');
      setPortalPassword('');
      setError(null);
      setMessage('Identifiant portail enregistré et chiffré en base (AES-256-GCM). La sonde peut être lancée.');
      void queryClient.invalidateQueries({ queryKey: ['portal-credentials'] });
    },
    onError: (err) => {
      setMessage(null);
      setError(String(err.message));
    },
  });

  const deleteCredential = useMutation({
    mutationFn: (key: string) =>
      fetchJson(`/api/v1/portals/credentials/${key}`, { method: 'DELETE' }),
    onSuccess: () => {
      setError(null);
      setMessage('Identifiants supprimés avec succès.');
      void queryClient.invalidateQueries({ queryKey: ['portal-credentials'] });
    },
    onError: (err) => {
      setMessage(null);
      setError(String(err.message));
    },
  });

  const testPortal = useMutation({
    mutationFn: (key: string) =>
      fetchJson<{ jobId: string | null }>('/api/v1/crawl/run', {
        method: 'POST',
        body: JSON.stringify({ sourceKey: key }),
      }),
    onSuccess: (result, key) => {
      setError(null);
      setMessage(`Sonde ${key} lancée (${result.jobId ?? 'en file'}) — le statut s’actualise automatiquement.`);
      void queryClient.invalidateQueries({ queryKey: ['portal-credentials'] });
    },
    onError: (err) => {
      setMessage(null);
      setError(String(err.message));
    },
  });

  // Test all configured portals
  const testAllConfigured = async () => {
    const configured = (credentialsQuery.data?.portals ?? []).filter((p) => p.configured);
    if (configured.length === 0) return;
    setError(null);
    setMessage(`Lancement de ${configured.length} sondes de vérification en arrière-plan…`);
    for (const p of configured) {
      try {
        await testPortal.mutateAsync(p.key);
      } catch {
        // continue with other portals
      }
    }
  };

  const portals = credentialsQuery.data?.portals ?? [];
  const configuredCount = portals.filter((p) => p.configured).length;
  const okProbesCount = portals.filter((p) => p.lastRun?.status === 'succeeded').length;
  const failedProbesCount = portals.filter((p) => p.lastRun?.status === 'failed').length;

  const filteredPortals = portals.filter((p) => {
    const matchesSearch =
      search === '' ||
      p.label.toLowerCase().includes(search.toLowerCase()) ||
      p.key.toLowerCase().includes(search.toLowerCase()) ||
      (p.username !== null && p.username.toLowerCase().includes(search.toLowerCase()));

    if (!matchesSearch) return false;

    if (filterState === 'configured') return p.configured;
    if (filterState === 'pending') return !p.configured;
    if (filterState === 'failed') return p.lastRun?.status === 'failed';
    return true;
  });

  return (
    <div className="space-y-5">
      <PageHeader
        title="Portails B2B & Grossistes"
        hint="Identifiants chiffrés (AES-256-GCM) et sondes Playwright de vérification de stock / prix auprès des grossistes."
        badge={
          <Badge tone={configuredCount > 0 ? 'good' : 'signal'}>
            {configuredCount} / {portals.length} configurés
          </Badge>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => queryClient.invalidateQueries({ queryKey: ['portal-credentials'] })}
              disabled={credentialsQuery.isFetching}
              className="gap-1.5"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${credentialsQuery.isFetching ? 'animate-spin' : ''}`} />
              <span>Actualiser</span>
            </Button>
            {isAdmin && configuredCount > 0 && (
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={testAllConfigured}
                disabled={testPortal.isPending}
                className="gap-1.5"
              >
                <PlugZap className="h-4 w-4" />
                <span>Tester tous les portails</span>
              </Button>
            )}
          </div>
        }
      />

      {/* Global Notifications */}
      {message !== null && (
        <Alert
          tone="good"
          title="Opération réussie"
          actions={<Button variant="ghost" size="xs" onClick={() => setMessage(null)}>Fermer</Button>}
        >
          {message}
        </Alert>
      )}
      {error !== null && (
        <Alert
          tone="alarm"
          title="Erreur"
          actions={<Button variant="ghost" size="xs" onClick={() => setError(null)}>Fermer</Button>}
        >
          {error}
        </Alert>
      )}

      {/* Master Key Security Diagnostic */}
      {credentialsQuery.data !== undefined && !credentialsQuery.data.masterKeySet ? (
        <Alert tone="alarm" title="Sécurité critique : Clé maîtresse absente">
          <p className="text-xs leading-relaxed">
            La variable d&apos;environnement <code>PORTAL_CREDENTIAL_KEY</code> est absente sur le conteneur serveur.
            Aucun identifiant ne peut être enregistré sans cette clé. Pour sécuriser le coffre-fort :
          </p>
          <code className="mt-2 block rounded-lg bg-rose-100/60 p-2 font-mono text-xs text-rose-950 border border-rose-300">
            openssl rand -hex 32
          </code>
        </Alert>
      ) : (
        <div className="flex items-center gap-2.5 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-2.5 text-xs text-emerald-950 font-medium">
          <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-600" />
          <span>
            <strong>Coffre-fort sécurisé</strong> : chiffrement matériel AES-256-GCM actif. Les mots de passe ne sont jamais exposés en clair ni renvoyés par l&apos;API.
          </span>
        </div>
      )}

      {/* Overview Statistics Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Panel className="p-3.5">
          <div className="flex items-center justify-between text-ink-ghost text-xs font-semibold">
            <span>Grossistes</span>
            <Server className="h-4 w-4" />
          </div>
          <div className="mt-1 text-2xl font-black tabular-nums text-ink">{portals.length}</div>
          <div className="text-[11px] text-ink-soft">Intégrations disponibles</div>
        </Panel>

        <Panel className="p-3.5">
          <div className="flex items-center justify-between text-emerald-700 text-xs font-semibold">
            <span>Configurés</span>
            <Lock className="h-4 w-4" />
          </div>
          <div className="mt-1 text-2xl font-black tabular-nums text-emerald-800">{configuredCount}</div>
          <div className="text-[11px] text-emerald-700">Identifiants prêts</div>
        </Panel>

        <Panel className="p-3.5">
          <div className="flex items-center justify-between text-sky-700 text-xs font-semibold">
            <span>Sondes OK</span>
            <PlugZap className="h-4 w-4" />
          </div>
          <div className="mt-1 text-2xl font-black tabular-nums text-sky-800">{okProbesCount}</div>
          <div className="text-[11px] text-sky-700">Accès grossiste validé</div>
        </Panel>

        <Panel className="p-3.5">
          <div className="flex items-center justify-between text-rose-700 text-xs font-semibold">
            <span>En échec</span>
            <AlertTriangle className="h-4 w-4" />
          </div>
          <div className="mt-1 text-2xl font-black tabular-nums text-rose-800">{failedProbesCount}</div>
          <div className="text-[11px] text-rose-700">À corriger ou vérifier</div>
        </Panel>
      </div>

      {/* Toolbar / Filters */}
      <Panel>
        <PanelBody className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[220px]">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-ghost" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un grossiste…"
                className="h-8.5 w-full rounded-lg border border-border bg-card pl-8 pr-3 text-xs text-ink placeholder:text-ink-ghost focus:border-herb-600 focus:outline-none"
              />
            </div>

            <div className="flex items-center rounded-lg border border-border bg-slate-100 p-0.5 text-xs">
              <button
                type="button"
                onClick={() => setFilterState('all')}
                className={`rounded-md px-2.5 py-1 font-semibold transition-all ${
                  filterState === 'all' ? 'bg-white text-ink shadow-2xs' : 'text-ink-soft hover:text-ink'
                }`}
              >
                Tous ({portals.length})
              </button>
              <button
                type="button"
                onClick={() => setFilterState('configured')}
                className={`rounded-md px-2.5 py-1 font-semibold transition-all ${
                  filterState === 'configured' ? 'bg-white text-emerald-900 shadow-2xs' : 'text-ink-soft hover:text-ink'
                }`}
              >
                Configurés ({configuredCount})
              </button>
              <button
                type="button"
                onClick={() => setFilterState('pending')}
                className={`rounded-md px-2.5 py-1 font-semibold transition-all ${
                  filterState === 'pending' ? 'bg-white text-amber-900 shadow-2xs' : 'text-ink-soft hover:text-ink'
                }`}
              >
                À configurer ({portals.length - configuredCount})
              </button>
              {failedProbesCount > 0 && (
                <button
                  type="button"
                  onClick={() => setFilterState('failed')}
                  className={`rounded-md px-2.5 py-1 font-semibold transition-all ${
                    filterState === 'failed' ? 'bg-white text-rose-900 shadow-2xs' : 'text-ink-soft hover:text-ink'
                  }`}
                >
                  En échec ({failedProbesCount})
                </button>
              )}
            </div>
          </div>

          <span className="text-xs font-semibold text-ink-soft tabular">
            {filteredPortals.length} portail{filteredPortals.length > 1 ? 's' : ''} affiché{filteredPortals.length > 1 ? 's' : ''}
          </span>
        </PanelBody>
      </Panel>

      {/* Main Portals List */}
      <Panel>
        <PanelBody className="space-y-3">
          <SectionLabel>Grossistes & Portails Partenaires</SectionLabel>

          {credentialsQuery.isPending && <Loading label="Lecture des identifiants et sondes…" />}
          {credentialsQuery.isError && (
            <Alert tone="alarm" title="Chargement impossible">
              {String(credentialsQuery.error.message)}
            </Alert>
          )}

          {filteredPortals.length === 0 && !credentialsQuery.isPending && (
            <div className="rounded-xl border border-dashed border-border py-8 text-center text-xs text-ink-soft">
              Aucun portail ne correspond à votre recherche.
            </div>
          )}

          <div className="space-y-2.5">
            {filteredPortals.map((portal) => (
              <div
                key={portal.key}
                className="rounded-xl border border-border bg-card p-3.5 transition-all shadow-2xs hover:border-slate-300"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-sky-50 text-sky-700 border border-sky-200">
                      <KeyRound className="h-4.5 w-4.5" />
                    </span>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-ink">{portal.label}</span>
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] font-bold text-slate-700 border border-slate-300">
                          {portal.key}
                        </span>
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-ink-soft">
                        {portal.username !== null ? (
                          <span className="font-mono text-[11px] text-ink font-semibold">
                            Compte : {portal.username}
                          </span>
                        ) : (
                          <span className="text-ink-ghost italic">Aucun identifiant enregistré</span>
                        )}
                        {portal.updatedAt !== null && (
                          <span className="text-[11px] text-ink-faint">
                            · Mis à jour le {new Date(portal.updatedAt).toLocaleDateString('fr-FR')}
                            {portal.updatedBy ? ` par ${portal.updatedBy}` : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Statuses & Action Badges */}
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusPill
                      tone={portal.configured ? 'ok' : 'warn'}
                      title={portal.configured ? 'Identifiants enregistrés' : 'Aucun identifiant'}
                    >
                      {portal.configured ? 'configuré' : 'à configurer'}
                    </StatusPill>

                    {portal.lastRun !== null ? (
                      <StatusPill
                        tone={
                          portal.lastRun.status === 'succeeded'
                            ? 'ok'
                            : portal.lastRun.status === 'running'
                              ? 'info'
                              : 'ko'
                        }
                        title={portal.lastRun.error ?? `Dernier test : ${portal.lastRun.startedAt}`}
                      >
                        {portal.lastRun.status === 'running'
                          ? 'sonde en cours…'
                          : portal.lastRun.status === 'succeeded'
                            ? 'sonde OK'
                            : 'sonde échouée'}
                      </StatusPill>
                    ) : (
                      <StatusPill tone="idle">jamais testé</StatusPill>
                    )}

                    {/* Operator Action Buttons */}
                    {isAdmin && (
                      <div className="flex items-center gap-1.5 ml-2 border-l border-border pl-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="xs"
                          disabled={testPortal.isPending || !portal.configured}
                          title={!portal.configured ? 'Configurez d’abord des identifiants' : 'Lancer un test Playwright immédiat'}
                          onClick={() => testPortal.mutate(portal.key)}
                          className="gap-1"
                        >
                          <PlugZap className="h-3.5 w-3.5 text-sky-700" />
                          <span>Tester</span>
                        </Button>
                        <Button
                          type="button"
                          variant="soft"
                          size="xs"
                          onClick={() => {
                            saveCredential.reset();
                            setEditingPortal(editingPortal?.key === portal.key ? null : portal);
                            setPortalUsername(portal.username ?? '');
                            setPortalPassword('');
                          }}
                        >
                          {editingPortal?.key === portal.key ? 'Fermer' : portal.configured ? 'Modifier' : 'Ajouter'}
                        </Button>
                        {portal.configured && (
                          <Button
                            type="button"
                            variant="danger"
                            size="xs"
                            disabled={deleteCredential.isPending}
                            onClick={() => {
                              if (window.confirm(`Confirmez-vous la suppression des identifiants de ${portal.label} ?`)) {
                                deleteCredential.mutate(portal.key);
                              }
                            }}
                            title="Supprimer les identifiants stockés"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Probe Error detail banner if failed */}
                {portal.lastRun?.status === 'failed' && portal.lastRun.error && (
                  <div className="mt-2.5 rounded-lg border border-rose-200 bg-rose-50/80 p-2 text-xs text-rose-950 font-medium flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 shrink-0 text-rose-600 mt-0.5" />
                    <div>
                      <strong>Échec de connexion Playwright :</strong> {portal.lastRun.error}
                    </div>
                  </div>
                )}

                {/* Inline Editing Form */}
                {editingPortal?.key === portal.key && (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      saveCredential.mutate({
                        key: portal.key,
                        username: portalUsername,
                        password: portalPassword,
                      });
                    }}
                    className="mt-3.5 rounded-xl border border-sky-200 bg-sky-50/40 p-4 space-y-3"
                  >
                    <div className="text-xs font-bold text-sky-950 flex items-center gap-1.5">
                      <Lock className="h-3.5 w-3.5 text-sky-700" />
                      <span>Configuration des accès pour {portal.label}</span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] font-bold text-ink-soft mb-1">
                          Nom d&apos;utilisateur / Email
                        </label>
                        <input
                          type="text"
                          required
                          value={portalUsername}
                          onChange={(e) => setPortalUsername(e.target.value)}
                          placeholder="ex. pharmacie@exemple.tn"
                          autoComplete="off"
                          className="h-9 w-full rounded-lg border border-border bg-card px-2.5 text-xs text-ink focus:border-herb-600 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-ink-soft mb-1">
                          Mot de passe {portal.configured && '(laisser vide pour ne pas modifier)'}
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            required={!portal.configured}
                            value={portalPassword}
                            onChange={(e) => setPortalPassword(e.target.value)}
                            placeholder="Mot de passe portail"
                            autoComplete="new-password"
                            className="h-9 w-full rounded-lg border border-border bg-card pl-2.5 pr-8 text-xs text-ink focus:border-herb-600 focus:outline-none"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((v) => !v)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-ghost hover:text-ink"
                          >
                            {showPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="xs"
                        onClick={() => {
                          setEditingPortal(null);
                          setPortalPassword('');
                        }}
                      >
                        Annuler
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        size="xs"
                        disabled={saveCredential.isPending || !credentialsQuery.data?.masterKeySet}
                        className="gap-1.5"
                      >
                        <Lock className="h-3 w-3" />
                        <span>{saveCredential.isPending ? 'Chiffrement…' : 'Enregistrer en sécurité'}</span>
                      </Button>
                    </div>

                    {saveCredential.isError && (
                      <Alert tone="alarm" title="Échec de l'enregistrement">
                        {String(saveCredential.error.message)}
                      </Alert>
                    )}
                  </form>
                )}
              </div>
            ))}
          </div>
        </PanelBody>
      </Panel>

      {/* Operator Safety & Anti-Scraping Guide */}
      <Panel>
        <PanelBody className="space-y-2 text-xs leading-relaxed text-ink-soft">
          <SectionLabel>Recommandations Opérationnelles & Sécurité</SectionLabel>
          <p>
            • <strong>Chiffrement au repos</strong> : Vos identifiants sont chiffrés avec l&apos;algorithme standard de l&apos;industrie <code>AES-256-GCM</code> avec vecteur d&apos;initialisation dynamique.
          </p>
          <p>
            • <strong>Sondes non intrusives</strong> : Le bouton « Tester » déclenche un crawl léger Playwright qui tente une authentification sur le portail du grossiste pour valider la validité du mot de passe et l&apos;absence de blocage IP ou de captcha.
          </p>
          <p>
            • <strong>Rotation des accès</strong> : Si votre mot de passe change sur le portail B2B, mettez-le à jour immédiatement ici pour éviter que les imports de stock du matin n&apos;échouent.
          </p>
        </PanelBody>
      </Panel>
    </div>
  );
}
